import { slicedToArray as _slicedToArray, extends as _extends, toConsumableArray as _toConsumableArray, objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState, useEffect } from 'react';
import { noop } from '../../utils/noop.js';
import kebabCase from 'lodash/kebabCase.js';
import { Stack } from '@chakra-ui/react';
import { i18n } from '../../i18n/translate.js';
import { Button } from '../Button/index.js';
import { DesignSystem } from '@everfi/design-system';
import { isFilterActive, getInitialFilters, filtersArrayToKeyValueObject, clearAllFilters, removeFilterFromCollection } from '../Filters/functions/index.js';
import { FilterChips } from '../Filters/FilterChips/index.js';
import { ConstructionCard } from '../ConstructionCard/component.js';
import { DateFilter } from '../Filters/DateFilter.js';
import { Modal } from '../../core/components/Modal/component.js';
import { Typography } from '../../core/components/Typography/component.js';
import { Layout } from '../../core/components/Layout/component.js';
import { ButtonVariant } from '../../core/components/Button/types.js';
import { ButtonGroup } from '../../core/components/ButtonGroup/component.js';

var FilterModal = function FilterModal(props) {
  var _props$applyButtonTex = props.applyButtonText,
      applyButtonText = _props$applyButtonTex === void 0 ? 'Apply Filters' : _props$applyButtonTex,
      _props$cancelButtonTe = props.cancelButtonText,
      cancelButtonText = _props$cancelButtonTe === void 0 ? 'Cancel' : _props$cancelButtonTe,
      _props$canClose = props.canClose,
      canClose = _props$canClose === void 0 ? true : _props$canClose,
      _props$className = props.className,
      className = _props$className === void 0 ? '' : _props$className,
      _props$container = props.container,
      container = _props$container === void 0 ? undefined : _props$container,
      _props$filterConfig = props.filterConfig,
      filterConfig = _props$filterConfig === void 0 ? [] : _props$filterConfig,
      initialFilters = props.initialFilters,
      modalProps = props.modalProps,
      onSelectFilters = props.onSelectFilters,
      _props$onClearFilters = props.onClearFilters,
      onClearFilters = _props$onClearFilters === void 0 ? noop : _props$onClearFilters,
      _props$onClose = props.onClose,
      onClose = _props$onClose === void 0 ? noop : _props$onClose,
      _props$show = props.show,
      show = _props$show === void 0 ? true : _props$show,
      title = props.title;

  var _useState = useState(filterConfig),
      _useState2 = _slicedToArray(_useState, 2),
      filters = _useState2[0],
      setFilters = _useState2[1];

  var _useState3 = useState([]),
      _useState4 = _slicedToArray(_useState3, 2),
      expandedFilters = _useState4[0],
      setExpandedFilters = _useState4[1];

  var activeFilters = filters.filter(function (filter) {
    return isFilterActive(filter);
  });
  useEffect(function () {
    var filters = getInitialFilters(filterConfig, initialFilters);
    setFilters(filters); // Ignore hooks warning as this logic is intended to be run on mount only.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  var handleApplyFilters = function handleApplyFilters() {
    onSelectFilters(filtersArrayToKeyValueObject(filters));
  };

  var clearFilters = function clearFilters() {
    var newFilters = clearAllFilters(filters);
    setFilters(newFilters);
    onClearFilters();
  };

  var removeFilter = function removeFilter(filterToRemove) {
    var newFilters = removeFilterFromCollection(filterToRemove, filters);
    setFilters(newFilters);
  };

  var onTogglePanel = function onTogglePanel(expanded, expandedFilter) {
    if (expanded) {
      setExpandedFilters([].concat(_toConsumableArray(expandedFilters), [expandedFilter]));
    } else {
      setExpandedFilters(expandedFilters.filter(function (filter) {
        return filter !== expandedFilter;
      }));
    }
  };

  var handleFilterChange = function handleFilterChange(updatedFilter, value) {
    var newFilters = filters.map(function (filter) {
      if (filter.name === updatedFilter.name) {
        if (filter.type === DateFilter) {
          filter.value = _objectSpread2(_objectSpread2({}, filter.value), value);
        } else {
          filter.value = value;
        }
      }

      return filter;
    });
    setFilters(newFilters);
  };

  var renderHeader = function renderHeader() {
    return /*#__PURE__*/React.createElement(Typography, {
      variant: "h2"
    }, title);
  };

  var renderBody = function renderBody() {
    return /*#__PURE__*/React.createElement(Stack, null, filters.map(function (filter) {
      var FilterComponent = filter.type;
      var selectedCount = Array.isArray(filter.value) ? filter.value.length : null;
      var selectedText = selectedCount !== null ? "".concat(selectedCount, " ").concat(i18n.t('selected')) : '';
      return /*#__PURE__*/React.createElement(ConstructionCard, {
        key: "panel_".concat(filter.name),
        onToggle: function onToggle(expanded) {
          return onTogglePanel(expanded, filter.name);
        },
        "data-action": "open-".concat(kebabCase(filter.name), "-panel"),
        title: filter.label,
        customActionBar: selectedText,
        overrideOverflowY: FilterComponent.displayName === 'DateFilter' ? 'visible' : 'scroll'
      }, /*#__PURE__*/React.createElement(FilterComponent, _extends({}, filter, {
        key: filter.name,
        name: filter.label,
        onChange: function onChange(value) {
          return handleFilterChange(filter, value);
        },
        disabled: !!filter.disabled,
        isWrapper: true,
        clearable: false
      })));
    }));
  };

  var renderFooter = function renderFooter() {
    return /*#__PURE__*/React.createElement(Layout, {
      className: "filter-modal",
      width: "full"
    }, activeFilters.length > 0 && /*#__PURE__*/React.createElement(Layout, {
      className: "selected-filters"
    }, /*#__PURE__*/React.createElement(Typography, {
      as: "p",
      textStyle: "ui-default",
      mb: "xs"
    }, i18n.t('Selected Filters')), /*#__PURE__*/React.createElement(FilterChips, {
      filters: activeFilters,
      onRemoveFilter: removeFilter
    })), /*#__PURE__*/React.createElement(Layout, {
      className: "footer-buttons",
      variant: "flex",
      justifyContent: "space-between",
      alignItems: "center"
    }, activeFilters.length > 0 && /*#__PURE__*/React.createElement(Button, {
      onClick: clearFilters,
      style: ButtonVariant.LINK,
      className: "clear-filters",
      "data-action": "clear-filters",
      icon: "xmark",
      label: i18n.t('Clear Filters')
    }), /*#__PURE__*/React.createElement(ButtonGroup, {
      gap: DesignSystem.Space.XS
    }, /*#__PURE__*/React.createElement(Button, {
      "data-action": "cancel-filters",
      label: cancelButtonText,
      onClick: onClose,
      style: ButtonVariant.SECONDARY
    }), /*#__PURE__*/React.createElement(Button, {
      "data-action": "apply-filters",
      label: applyButtonText,
      onClick: handleApplyFilters,
      style: ButtonVariant.PRIMARY
    }))));
  };

  return /*#__PURE__*/React.createElement(Modal, {
    className: className,
    containerRef: container,
    footer: renderFooter(),
    header: renderHeader(),
    id: modalProps === null || modalProps === void 0 ? void 0 : modalProps.id,
    isCentered: true,
    isOpen: show,
    modalType: "modal",
    onClose: onClose,
    showCloseButton: canClose,
    size: modalProps === null || modalProps === void 0 ? void 0 : modalProps.bsSize,
    variant: "default"
  }, renderBody());
};

FilterModal.displayName = "FilterModal";
export { FilterModal };